import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../modal/ModalShell";
import { AdminUser, adminUserDisplayName } from "../../features/admin/models/AdminUser";
import { SetCustomPriceRequest } from "../../features/admin/contracts/SetCustomPriceRequest";

interface CustomPriceModalProps {
  user: AdminUser;
  busy: boolean;
  /** Applies the override, or null to clear it back to list pricing. */
  onApply: (request: SetCustomPriceRequest | null) => void;
  onClose: () => void;
}

const toMajorInput = (minorUnits: number | null): string =>
  minorUnits == null ? "" : (minorUnits / 100).toString();

/** "9.99" → 999; empty → null; anything unparsable → NaN (blocks apply). */
const toMinorUnits = (input: string): number | null => {
  const trimmed = input.trim().replace(",", ".");
  if (trimmed === "") {
    return null;
  }
  return Math.round(Number(trimmed) * 100);
};

const isValidAmount = (minorUnits: number | null): boolean =>
  minorUnits === null || (Number.isFinite(minorUnits) && minorUnits > 0);

/**
 * Sets a per-account subscription price override — flexible, individually
 * negotiated pricing. A missing amount leaves that billing cycle at the list
 * price; clearing falls back to list pricing entirely.
 */
const CustomPriceModal: React.FC<CustomPriceModalProps> = ({ user, busy, onApply, onClose }) => {
  const { t } = useTranslation();
  const [monthly, setMonthly] = useState(toMajorInput(user.customMonthlyAmountMinorUnits));
  const [annual, setAnnual] = useState(toMajorInput(user.customAnnualAmountMinorUnits));
  const [currency, setCurrency] = useState(user.customPriceCurrency ?? "EUR");

  const monthlyMinor = toMinorUnits(monthly);
  const annualMinor = toMinorUnits(annual);
  const canApply =
    !busy &&
    isValidAmount(monthlyMinor) &&
    isValidAmount(annualMinor) &&
    (monthlyMinor !== null || annualMinor !== null);

  const hasOverride = user.customPriceCurrency !== null;

  return (
    <ModalShell onClose={onClose} labelledBy="custom-price-title">
      <div className="p-6">
        <h2 id="custom-price-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("admin.customPrice.title")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t("admin.customPrice.subtitle", { name: adminUserDisplayName(user) })}
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
            {t("admin.customPrice.monthlyLabel")}
            <input
              className="input mt-1"
              inputMode="decimal"
              value={monthly}
              onChange={(event) => setMonthly(event.target.value)}
              placeholder="9.99"
            />
          </label>
          <label className="block text-sm font-medium text-grey-30 dark:text-grey-70">
            {t("admin.customPrice.annualLabel")}
            <input
              className="input mt-1"
              inputMode="decimal"
              value={annual}
              onChange={(event) => setAnnual(event.target.value)}
              placeholder="99"
            />
          </label>
        </div>

        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("admin.customPrice.currencyLabel")}
          <select
            className="input mt-1"
            value={currency}
            onChange={(event) => setCurrency(event.target.value)}
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
        </label>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {hasOverride ? (
            <button
              type="button"
              className="text-sm font-medium text-red-700 hover:underline dark:text-red-400"
              disabled={busy}
              onClick={() => onApply(null)}
            >
              {t("admin.customPrice.clear")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canApply}
              onClick={() =>
                onApply({
                  monthlyAmountMinorUnits: monthlyMinor,
                  annualAmountMinorUnits: annualMinor,
                  currency,
                })
              }
            >
              {t("admin.customPrice.set")}
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default CustomPriceModal;
